import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, Chip, TextField, MenuItem, Button, Stack,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TablePagination, Alert
} from '@mui/material';
import {
  History as HistoryIcon, Visibility as ViewIcon, Refresh as RefreshIcon,
  Close as CloseIcon, FilterList as FilterIcon, Restore as RestoreIcon
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppSelector } from '../../hooks/redux.hooks';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';

interface AuditLogEntry {
  auditLogId: number;
  entityType: string;
  entityId: number;
  action: string;
  changedFields?: string | null;
  userId?: number | null;
  userName?: string | null;
  fullName?: string | null;
  changedAt: string;
  ipAddress?: string | null;
  reason?: string | null;
  hasOldValues: boolean;
  hasNewValues: boolean;
}

interface AuditLogDetail extends AuditLogEntry {
  oldValuesJson?: string | null;
  newValuesJson?: string | null;
}

const t = {
  ar: {
    title: 'سجل التدقيق (Audit Trail)',
    subtitle: 'تاريخ تعديلات القيود والسندات والفواتير',
    refresh: 'تحديث',
    filters: 'فلاتر',
    entityType: 'النوع', entityId: 'المعرّف', action: 'العملية',
    user: 'المستخدم', date: 'التاريخ', changedFields: 'الحقول المعدّلة',
    ip: 'IP', view: 'عرض التفاصيل', actions: 'الإجراءات',
    clearFilters: 'إعادة تعيين',
    none: 'لا توجد سجلات',
    detailsTitle: 'تفاصيل التغيير',
    before: 'القيم قبل التعديل',
    after: 'القيم بعد التعديل',
    close: 'إغلاق',
    restore: 'استرداد القيم القديمة',
    restoreConfirm: 'سيتم استرداد القيد إلى حالته في هذا السجل. القيود الـ Posted لا يمكن استردادها. هل تريد المتابعة؟',
    restoreSuccess: 'تم استرداد القيد بنجاح',
    restoreError: 'فشل الاسترداد',
    restoring: 'جاري الاسترداد...',
    actionUpdate: 'تعديل', actionDelete: 'حذف',
    actionPost: 'ترحيل', actionUnpost: 'إلغاء ترحيل', actionCreate: 'إنشاء',
    typeJE: 'قيد محاسبي', typeVoucher: 'سند',
    rowsPerPage: 'الصفوف:',
    header: 'بيانات المستند', lines: 'السطور / التفاصيل',
    field: 'الحقل', oldValue: 'القيمة القديمة', newValue: 'القيمة الجديدة',
    deleted: '— محذوف —', newlyAdded: '— جديد —', noChange: 'لا تغيير', changed: 'تغيّر',
    lineNumber: 'سطر',
  },
  en: {
    title: 'Audit Trail',
    subtitle: 'History of modifications to JEs, Vouchers, Invoices',
    refresh: 'Refresh', filters: 'Filters',
    entityType: 'Type', entityId: 'ID', action: 'Action',
    user: 'User', date: 'Date', changedFields: 'Changed Fields',
    ip: 'IP', view: 'View Details', actions: 'Actions',
    clearFilters: 'Clear',
    none: 'No records found',
    detailsTitle: 'Change Details',
    before: 'Values Before',
    after: 'Values After',
    close: 'Close',
    restore: 'Restore Old Values',
    restoreConfirm: 'This will restore the entry to the state captured in this audit row. Posted entries cannot be restored. Continue?',
    restoreSuccess: 'Entry restored successfully',
    restoreError: 'Restore failed',
    restoring: 'Restoring...',
    actionUpdate: 'Update', actionDelete: 'Delete',
    actionPost: 'Post', actionUnpost: 'Unpost', actionCreate: 'Create',
    typeJE: 'Journal Entry', typeVoucher: 'Voucher',
    rowsPerPage: 'Rows:',
    header: 'Header', lines: 'Lines / Details',
    field: 'Field', oldValue: 'Old Value', newValue: 'New Value',
    deleted: '— deleted —', newlyAdded: '— new —', noChange: 'no change', changed: 'changed',
    lineNumber: 'Line',
  }
};

// ── Field name translation map ────────────────────────────────────
const FIELD_LABELS_AR: Record<string, string> = {
  // JE Header
  JournalEntryId: 'رقم القيد', EntryNumber: 'رقم المستند', EntryDate: 'تاريخ القيد',
  EntryType: 'نوع القيد', Description: 'الوصف', DescriptionAr: 'الوصف بالعربي',
  Status: 'الحالة', PostedDate: 'تاريخ الترحيل', PostedBy: 'رحّل بواسطة',
  TotalDebit: 'إجمالي المدين', TotalCredit: 'إجمالي الدائن', Notes: 'ملاحظات',
  FiscalPeriodId: 'السنة المالية', SourceType: 'نوع المصدر', SourceDocumentId: 'مرجع المصدر',
  // JE Detail
  EntryDetailId: 'رقم السطر الداخلي', LineNumber: 'رقم السطر', AccountId: 'الحساب',
  Debit: 'مدين', Credit: 'دائن', CostCenterId: 'مركز التكلفة',
  // Voucher
  VoucherId: 'رقم السند', VoucherNumber: 'رقم المستند', VoucherDate: 'تاريخ السند',
  VoucherType: 'نوع السند', PayeeType: 'نوع الطرف', PayeeId: 'معرّف الطرف',
  PayeeName: 'اسم الطرف', Amount: 'المبلغ', CurrencyCode: 'العملة',
  ExchangeRate: 'سعر الصرف', AmountInBaseCurrency: 'المبلغ بالعملة الأساسية',
  PaymentMethod: 'طريقة الدفع', ReferenceNumber: 'الرقم المرجعي',
  BankAccountId: 'الحساب البنكي', ChequeNumber: 'رقم الشيك',
  ChequeDate: 'تاريخ الشيك', BankName: 'البنك',
  // Voucher Detail
  VoucherDetailId: 'رقم السطر الداخلي', DocumentType: 'نوع المستند',
  DocumentId: 'معرّف المستند', DocumentNumber: 'رقم المستند المرتبط',
  AllocatedAmount: 'المبلغ المخصص', Side: 'الجهة',
};

const FIELD_LABELS_EN: Record<string, string> = {
  // we'll just use the English property names by default
};

const ENTITY_TYPES = ['JournalEntry', 'Voucher', 'PurchaseInvoice', 'StockIssue', 'DailySale'];
const ACTIONS = ['Update', 'Delete', 'Post', 'Unpost'];

const AuditLogPage: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';
  const tt = t[language as 'ar' | 'en'];
  const isSuperAdmin = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
  const confirm = useConfirm();
  const [restoring, setRestoring] = useState(false);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [filterEntityType, setFilterEntityType] = useState<string>('');
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterEntityId, setFilterEntityId] = useState<string>('');

  const [detail, setDetail] = useState<AuditLogDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', rowsPerPage.toString());
      params.set('skip', (page * rowsPerPage).toString());
      if (filterEntityType) params.set('entityType', filterEntityType);
      if (filterAction) params.set('action', filterAction);
      if (filterEntityId) params.set('entityId', filterEntityId);
      const { data } = await axiosInstance.get(`/AuditLog?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, rowsPerPage]);

  const applyFilters = () => {
    setPage(0);
    load();
  };

  const clearFilters = () => {
    setFilterEntityType('');
    setFilterAction('');
    setFilterEntityId('');
    setPage(0);
    setTimeout(load, 0);
  };

  const openDetail = async (id: number) => {
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const { data } = await axiosInstance.get(`/AuditLog/${id}`);
      setDetail(data);
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const handleRestore = async () => {
    if (!detail) return;
    const ok = await confirm({ message: tt.restoreConfirm, severity: 'warning' });
    if (!ok) return;
    setRestoring(true);
    try {
      await axiosInstance.post(`/AuditLog/${detail.auditLogId}/restore`);
      toast.success(tt.restoreSuccess);
      setDetailOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || tt.restoreError);
    } finally {
      setRestoring(false);
    }
  };

  // Restore is only available for: Super Admin + Update action + JournalEntry (for now)
  const canRestore = (d: AuditLogDetail | null): boolean => {
    if (!d || !isSuperAdmin) return false;
    if (d.action !== 'Update') return false;
    if (d.entityType !== 'JournalEntry') return false;
    if (!d.oldValuesJson) return false;
    return true;
  };

  const fmt = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString(isRTL ? 'ar-KW' : 'en-GB',
      { dateStyle: 'short', timeStyle: 'medium', timeZone: 'Asia/Kuwait' }) : '-';

  const entityTypeLabel = (et: string) => {
    if (et === 'JournalEntry') return tt.typeJE;
    if (et === 'Voucher') return tt.typeVoucher;
    return et;
  };

  const actionLabel = (a: string) => {
    if (a === 'Update') return tt.actionUpdate;
    if (a === 'Delete') return tt.actionDelete;
    if (a === 'Post') return tt.actionPost;
    if (a === 'Unpost') return tt.actionUnpost;
    if (a === 'Create') return tt.actionCreate;
    return a;
  };

  const actionColor = (a: string) => {
    if (a === 'Delete') return '#e53935';
    if (a === 'Post') return '#43a047';
    if (a === 'Unpost') return '#fb8c00';
    return '#1976d2';
  };

  const prettyJson = (json?: string | null) => {
    if (!json) return '-';
    try { return JSON.stringify(JSON.parse(json), null, 2); }
    catch { return json; }
  };

  // Parse JSON safely → returns Header + Details if our snapshot shape, else raw
  const parseSnapshot = (json?: string | null): { header?: Record<string, any>; details?: any[]; raw?: any } => {
    if (!json) return {};
    try {
      const obj = JSON.parse(json);
      if (obj && typeof obj === 'object' && 'Header' in obj) {
        return { header: obj.Header || {}, details: Array.isArray(obj.Details) ? obj.Details : [] };
      }
      return { raw: obj };
    } catch { return {}; }
  };

  const fieldLabel = (key: string): string => {
    if (isRTL && FIELD_LABELS_AR[key]) return FIELD_LABELS_AR[key];
    return FIELD_LABELS_EN[key] || key;
  };

  // Pretty value formatting (dates, numbers, nulls)
  const fmtValue = (v: any): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? '✓' : '✗';
    if (typeof v === 'number') return v.toString();
    if (typeof v === 'string') {
      // ISO datetime detection
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
        try { return new Date(v).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }); }
        catch { return v; }
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        try { return new Date(v).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB'); }
        catch { return v; }
      }
      return v;
    }
    if (Array.isArray(v)) return `[${v.length} items]`;
    return JSON.stringify(v);
  };

  // Skip these noisy/internal fields from display
  const HIDDEN_FIELDS = new Set(['EntryDetailId', 'VoucherDetailId']);

  const renderHeaderComparison = (oldObj: Record<string, any> | undefined, newObj: Record<string, any> | undefined) => {
    const keys = new Set<string>();
    if (oldObj) Object.keys(oldObj).forEach(k => keys.add(k));
    if (newObj) Object.keys(newObj).forEach(k => keys.add(k));
    const filtered = Array.from(keys).filter(k => !HIDDEN_FIELDS.has(k));

    if (filtered.length === 0) return null;

    return (
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#eceff1' }}>
            <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>{tt.field}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '37.5%' }}>{tt.oldValue}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '37.5%' }}>{tt.newValue}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map(k => {
            const oldV = oldObj?.[k];
            const newV = newObj?.[k];
            const changed = JSON.stringify(oldV) !== JSON.stringify(newV);
            return (
              <TableRow key={k} sx={{
                bgcolor: changed
                  ? (isDark ? 'rgba(255,193,7,0.10)' : 'rgba(255,193,7,0.10)')
                  : 'transparent'
              }}>
                <TableCell sx={{ fontWeight: 600 }}>{fieldLabel(k)}</TableCell>
                <TableCell sx={{
                  color: changed ? (isDark ? '#ef9a9a' : '#c62828') : 'text.primary',
                  textDecoration: changed && oldV != null ? 'line-through' : 'none',
                  fontWeight: changed ? 600 : 400
                }}>{fmtValue(oldV)}</TableCell>
                <TableCell sx={{
                  color: changed ? (isDark ? '#a5d6a7' : '#2e7d32') : 'text.primary',
                  fontWeight: changed ? 700 : 400
                }}>{fmtValue(newV)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const renderDetailsComparison = (oldRows: any[] | undefined, newRows: any[] | undefined) => {
    const oldArr = oldRows || [];
    const newArr = newRows || [];
    if (oldArr.length === 0 && newArr.length === 0) return null;

    // Match rows by LineNumber when available
    const allLineNumbers = new Set<number>();
    oldArr.forEach((r: any) => { if (r?.LineNumber != null) allLineNumbers.add(r.LineNumber); });
    newArr.forEach((r: any) => { if (r?.LineNumber != null) allLineNumbers.add(r.LineNumber); });

    const lineNumbers = Array.from(allLineNumbers).sort((a, b) => a - b);

    return (
      <Stack spacing={1.5}>
        {lineNumbers.map(ln => {
          const oldRow = oldArr.find((r: any) => r?.LineNumber === ln);
          const newRow = newArr.find((r: any) => r?.LineNumber === ln);
          return (
            <Paper key={ln} sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` }}>
              <Typography variant="caption" fontWeight="bold" sx={{ color: '#1976d2', mb: 1, display: 'block' }}>
                {tt.lineNumber} #{ln} {!oldRow && <Chip label={tt.newlyAdded} size="small" sx={{ ml: 1, bgcolor: '#43a047', color: '#fff', fontSize: '0.6rem', height: 18 }} />}
                {!newRow && <Chip label={tt.deleted} size="small" sx={{ ml: 1, bgcolor: '#e53935', color: '#fff', fontSize: '0.6rem', height: 18 }} />}
              </Typography>
              {renderHeaderComparison(oldRow, newRow)}
            </Paper>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(33,150,243,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HistoryIcon sx={{ color: '#2196f3', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              {tt.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">{tt.subtitle}</Typography>
          </Box>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
          <FilterIcon sx={{ color: 'text.secondary' }} />
          <TextField select size="small" label={tt.entityType} value={filterEntityType}
            onChange={(e) => setFilterEntityType(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
            {ENTITY_TYPES.map(et => <MenuItem key={et} value={et}>{entityTypeLabel(et)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label={tt.action} value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
            {ACTIONS.map(a => <MenuItem key={a} value={a}>{actionLabel(a)}</MenuItem>)}
          </TextField>
          <TextField size="small" label={tt.entityId} value={filterEntityId} type="number"
            onChange={(e) => setFilterEntityId(e.target.value)} sx={{ minWidth: 120 }} />
          <Button variant="contained" onClick={applyFilters}>{tt.filters}</Button>
          <Button variant="outlined" onClick={clearFilters}>{tt.clearFilters}</Button>
          <Box flex={1} />
          <Tooltip title={tt.refresh}>
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box py={6}><Alert severity="info">{tt.none}</Alert></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                {[tt.date, tt.entityType, tt.entityId, tt.action, tt.user, tt.changedFields, tt.ip, tt.actions].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.auditLogId} hover>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{fmt(r.changedAt)}</Typography></TableCell>
                  <TableCell><Chip label={entityTypeLabel(r.entityType)} size="small" sx={{ fontSize: '0.7rem' }} /></TableCell>
                  <TableCell><Typography variant="caption" fontWeight={600}>#{r.entityId}</Typography></TableCell>
                  <TableCell><Chip label={actionLabel(r.action)} size="small" sx={{ bgcolor: actionColor(r.action), color: '#fff', fontSize: '0.7rem', fontWeight: 600 }} /></TableCell>
                  <TableCell>
                    <Typography variant="caption" fontWeight={600}>{r.userName || '-'}</Typography>
                    {r.fullName && <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{r.fullName}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.changedFields || ''}>
                      <Typography variant="caption" sx={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.changedFields || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{r.ipAddress || '-'}</Typography></TableCell>
                  <TableCell>
                    <Tooltip title={tt.view}>
                      <IconButton size="small" onClick={() => openDetail(r.auditLogId)} sx={{ color: '#1976d2' }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage={tt.rowsPerPage}
        />
      </Paper>

      {/* ── Detail Dialog ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Typography fontWeight="bold">
              {tt.detailsTitle} {detail ? `— ${entityTypeLabel(detail.entityType)} #${detail.entityId} (${actionLabel(detail.action)})` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setDetailOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : !detail ? null : (
            <Stack spacing={2}>
              <Paper sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary">{tt.date}</Typography>
                    <Typography variant="body2" fontWeight={600}>{fmt(detail.changedAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{tt.user}</Typography>
                    <Typography variant="body2" fontWeight={600}>{detail.userName || '-'} {detail.fullName ? `(${detail.fullName})` : ''}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{tt.ip}</Typography>
                    <Typography variant="body2" fontFamily="monospace">{detail.ipAddress || '-'}</Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">{tt.changedFields}</Typography>
                    <Typography variant="body2">{detail.changedFields || '-'}</Typography>
                  </Box>
                </Stack>
              </Paper>

              {(() => {
                const oldSnap = parseSnapshot(detail.oldValuesJson);
                const newSnap = parseSnapshot(detail.newValuesJson);
                const hasStructured = !!(oldSnap.header || newSnap.header);
                const hasRaw = !!(oldSnap.raw || newSnap.raw);

                if (hasStructured) {
                  return (
                    <>
                      {/* Header comparison */}
                      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, color: '#1976d2' }}>
                          📋 {tt.header}
                        </Typography>
                        {renderHeaderComparison(oldSnap.header, newSnap.header)}
                      </Paper>

                      {/* Details (lines) comparison */}
                      {((oldSnap.details?.length || 0) + (newSnap.details?.length || 0)) > 0 && (
                        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, color: '#1976d2' }}>
                            📑 {tt.lines}
                          </Typography>
                          {renderDetailsComparison(oldSnap.details, newSnap.details)}
                        </Paper>
                      )}
                    </>
                  );
                }

                // Fallback for non-structured data (Delete without snapshot, raw object)
                if (hasRaw) {
                  return (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      <Paper sx={{ p: 2, bgcolor: isDark ? 'rgba(244,67,54,0.08)' : '#fff5f5', border: `1px solid ${isDark ? 'rgba(244,67,54,0.3)' : '#ffcdd2'}` }}>
                        <Typography variant="subtitle2" fontWeight="bold" color={isDark ? '#ef9a9a' : '#c62828'} mb={1}>
                          🔴 {tt.before}
                        </Typography>
                        <Box component="pre" sx={{
                          m: 0, fontSize: '0.75rem', fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                          maxHeight: 500, overflow: 'auto',
                          color: isDark ? '#e0e0e0' : '#212121',
                          bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#fff', p: 1.5, borderRadius: 1
                        }}>
                          {prettyJson(detail.oldValuesJson)}
                        </Box>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#f1f8e9', border: `1px solid ${isDark ? 'rgba(76,175,80,0.3)' : '#c5e1a5'}` }}>
                        <Typography variant="subtitle2" fontWeight="bold" color={isDark ? '#a5d6a7' : '#2e7d32'} mb={1}>
                          🟢 {tt.after}
                        </Typography>
                        <Box component="pre" sx={{
                          m: 0, fontSize: '0.75rem', fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                          maxHeight: 500, overflow: 'auto',
                          color: isDark ? '#e0e0e0' : '#212121',
                          bgcolor: isDark ? 'rgba(0,0,0,0.3)' : '#fff', p: 1.5, borderRadius: 1
                        }}>
                          {prettyJson(detail.newValuesJson)}
                        </Box>
                      </Paper>
                    </Box>
                  );
                }

                return <Alert severity="info">{tt.none}</Alert>;
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setDetailOpen(false)}>{tt.close}</Button>
          {canRestore(detail) && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<RestoreIcon />}
              onClick={handleRestore}
              disabled={restoring}
              sx={{ fontWeight: 'bold', minWidth: 220 }}
            >
              {restoring ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `↻ ${tt.restore}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogPage;
